import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import Icon from "../../Components/Icons";
import { brand } from "../../brand";
import { color, fontFamily, radius } from "../../Components/Theme";
import { useBusinessAccountSelector } from "../../redux/selectors/useBusinessAccountSelectors";
import { useNavItems, isActive } from "./navItems";

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_WIDTH_COLLAPSED = 72;

const Aside = styled.aside`
  position: sticky;
  top: 0;
  align-self: flex-start;
  flex: 0 0 ${(p) => (p.$collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH)}px;
  width: ${(p) => (p.$collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH)}px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${color("surface")};
  border-right: 1px solid ${color("border")};
  font-family: ${() => fontFamily()};
  transition: width 180ms ease-out, flex-basis 180ms ease-out;
  z-index: 10;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  height: 64px;
  padding: 0 ${(p) => (p.$collapsed ? "8px" : "20px")};
  flex-shrink: 0;
  img { height: 40px; max-width: 100%; object-fit: contain; }
`;

const Nav = styled.nav`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px ${(p) => (p.$collapsed ? "12px" : "12px")} 16px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${color("border")};
  margin: 12px 8px;
`;

const SectionLabel = styled(Link)`
  display: block;
  margin: 4px 12px 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${color("text-muted")};
  text-decoration: none;
  text-align: ${(p) => (p.$collapsed ? "center" : "left")};
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: 2px; border-radius: 4px; }
`;

const Item = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  margin-bottom: 4px;
  padding: 0 ${(p) => (p.$collapsed ? "0" : "12px")};
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  border-radius: ${() => radius("control")}px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
  padding-top: 8px;
  padding-bottom: 8px;
  color: ${(p) => (p.$active ? color("primary") : color("text"))};
  background: ${(p) => (p.$active ? color("primary-light") : "transparent")};
  transition: background-color 150ms ease-out;
  &:hover { background: ${(p) => (p.$active ? color("primary-light") : color("list"))}; }
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: 2px; }
  &::before {
    content: "";
    position: absolute;
    left: -12px;
    top: 8px;
    bottom: 8px;
    width: 4px;
    border-radius: 0 4px 4px 0;
    background: ${(p) => (p.$active ? color("primary") : "transparent")};
  }
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$collapsed ? "center" : "flex-start")};
  gap: 12px;
  min-height: 44px;
  margin: 0 12px 12px;
  padding: 0 ${(p) => (p.$collapsed ? "0" : "12px")};
  border: 0;
  border-top: 1px solid ${color("border")};
  border-radius: ${() => radius("control")}px;
  background: transparent;
  color: ${color("text-muted")};
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  &:hover { background: ${color("list")}; }
  &:focus-visible { outline: 2px solid ${color("primary")}; outline-offset: 2px; }
`;

const SideBar = ({ selected, sideBarCollapse, handleSideBar }) => {
  const items = useNavItems();
  const businessAccount = useBusinessAccountSelector();
  const collapsed = !!sideBarCollapse;

  return (
    <Aside $collapsed={collapsed}>
      <LogoRow $collapsed={collapsed}>
        <img src={businessAccount?.businessLogo ? businessAccount.businessLogo : brand.logo} alt="" />
      </LogoRow>

      <Nav $collapsed={collapsed} aria-label="Main">
        {items.map((value, index) => {
          const prev = items[index - 1];
          const startsSection = value.section && prev?.section !== value.section;
          const startsGroup = prev && prev.group !== value.group && !startsSection;
          const active = isActive(selected, value.href);
          const link = (
            <Item
              to={value.href}
              $active={active}
              $collapsed={collapsed}
              aria-current={active ? "page" : undefined}
              aria-label={collapsed ? value.name : undefined}
            >
              <Icon $icon={value.icon} $size="22px" $color={active ? "primary" : "text-muted"} />
              {!collapsed && <span>{value.name}</span>}
            </Item>
          );
          return (
            <React.Fragment key={value.href}>
              {startsGroup && <Divider />}
              {startsSection && (
                <>
                  {prev && <Divider />}
                  <SectionLabel to="/care-records" $collapsed={collapsed}>
                    {collapsed ? "···" : value.section.toUpperCase()}
                  </SectionLabel>
                </>
              )}
              {collapsed ? (
                <Tooltip title={value.name} placement="right" arrow>
                  {link}
                </Tooltip>
              ) : (
                link
              )}
            </React.Fragment>
          );
        })}
      </Nav>

      <Toggle
        type="button"
        $collapsed={collapsed}
        onClick={() => handleSideBar()}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!collapsed}
      >
        <Icon $icon={collapsed ? "KeyboardDoubleArrowRight" : "KeyboardDoubleArrowLeft"} $size="22px" $color="text-muted" />
        {!collapsed && <span>Collapse</span>}
      </Toggle>
    </Aside>
  );
};

export default SideBar;
